import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Send, LogOut, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AdminTabs from "@/components/AdminTabs";

const CATEGORY_FALLBACK = [
  "Policy",
  "Drilldown",
  "Commodities",
  "Capital Markets",
  "Projects",
  "Companies",
  "Regions",
];

const emptyForm = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  category: "Drilldown",
  region: "",
  commodity: "",
  company: "",
  project: "",
  author: "BedrockBulletin staff",
  featuredImage: "",
};

const nullifyEmpties = (form) => ({
  ...form,
  slug: form.slug.trim() || null,
  region: form.region.trim() || null,
  commodity: form.commodity.trim() || null,
  company: form.company.trim() || null,
  project: form.project.trim() || null,
  featuredImage: form.featuredImage.trim() || null,
});

export default function AdminDraftEditorPage() {
  const { draftId } = useParams();
  const isNew = !draftId || draftId === "new";
  const nav = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [categories, setCategories] = useState(CATEGORY_FALLBACK);
  const [regions, setRegions] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [current, setCurrent] = useState(null); // full server-side record

  // Bootstrap taxonomies + existing draft
  useEffect(() => {
    api.get("/admin/drafts/categories", { withCredentials: true })
      .then((r) => setCategories(r.data?.categories || CATEGORY_FALLBACK))
      .catch(() => { /* keep fallback */ });
    Promise.all([
      api.get("/regions").then((r) => r.data).catch(() => []),
      api.get("/commodities").then((r) => r.data).catch(() => []),
      api.get("/companies").then((r) => r.data).catch(() => []),
      api.get("/projects").then((r) => r.data).catch(() => []),
    ]).then(([rg, cm, co, pr]) => {
      setRegions(Array.isArray(rg) ? rg : []);
      setCommodities(Array.isArray(cm) ? cm : []);
      setCompanies(Array.isArray(co) ? co : []);
      setProjects(Array.isArray(pr) ? pr : []);
    });
  }, []);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api.get(`/admin/drafts/${draftId}`, { withCredentials: true })
      .then((r) => {
        const d = r.data || {};
        setCurrent(d);
        setForm({
          title: d.title || "",
          slug: d.slug || "",
          excerpt: d.excerpt || "",
          content: d.content || "",
          category: d.category || "Drilldown",
          region: d.region || "",
          commodity: d.commodity || "",
          company: d.company || "",
          project: d.project || "",
          author: d.author || "BedrockBulletin staff",
          featuredImage: d.featuredImage || "",
        });
      })
      .catch(() => toast.error("Failed to load draft"))
      .finally(() => setLoading(false));
  }, [draftId, isNew]);

  const setField = useCallback((k) => (e) => {
    const v = e?.target?.value ?? e;
    setForm((f) => ({ ...f, [k]: v }));
  }, []);

  const canSave = form.title.trim().length >= 3 && form.category;

  const save = async (opts = { publishAfter: false }) => {
    if (!canSave) {
      toast.error("Title (3+ chars) and category are required");
      return null;
    }
    setSaving(true);
    try {
      const body = nullifyEmpties(form);
      let record;
      if (isNew) {
        record = (await api.post("/admin/drafts", body, { withCredentials: true })).data;
        setCurrent(record);
        // Move to the edit URL so subsequent saves are updates
        nav(`/admin/drafts/${record.id}`, { replace: true });
      } else {
        record = (await api.put(`/admin/drafts/${draftId}`, body, { withCredentials: true })).data;
        setCurrent(record);
      }
      toast.success("Saved");
      if (opts.publishAfter) {
        setPublishing(true);
        try {
          await api.post(`/admin/drafts/${record.id}/publish`, null, { withCredentials: true });
          toast.success("Published — live on /news");
          nav("/admin/drafts");
        } catch { toast.error("Publish failed"); } finally { setPublishing(false); }
      }
      return record;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout", null, { withCredentials: true }); } catch { /* ignore */ }
    window.location.href = "/login";
  };

  const contentPreviewParagraphs = useMemo(
    () => (form.content || "").split(/\n+/).map((p) => p.trim()).filter(Boolean),
    [form.content],
  );

  return (
    <div data-testid="admin-draft-editor-page" className="bg-bb-offwhite min-h-screen">
      <header className="sticky top-0 z-40 bg-white border-b border-border">
        <div className="bb-container py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="bb-overline text-bb-copper">Admin · Drafts</div>
            <div className="font-archivo font-bold text-bb-navy text-lg leading-tight truncate">
              {isNew ? "New draft" : form.title || "Untitled"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/drafts"
              data-testid="admin-drafts-back"
              className="inline-flex items-center gap-1 px-3 py-2 text-[11px] font-archivo uppercase tracking-widest text-bb-slate hover:text-bb-navy"
            >
              <ArrowLeft size={13} /> <span className="hidden sm:inline">Back</span>
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1 px-3 py-2 text-[11px] font-archivo uppercase tracking-widest text-bb-slate hover:text-bb-navy"
            >
              <LogOut size={13} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
        <AdminTabs />
      </header>

      <div className="bb-container py-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="lg:col-span-3 text-center py-10 bb-overline text-bb-slate">Loading…</div>
        ) : (
          <>
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white border border-border p-4 space-y-3">
                <label className="block">
                  <span className="bb-overline text-bb-slate">Title</span>
                  <input
                    type="text"
                    value={form.title}
                    onChange={setField("title")}
                    data-testid="draft-title"
                    className="mt-1 w-full border border-border px-3 py-2.5 text-lg font-archivo font-semibold text-bb-navy focus:outline-none focus:border-bb-copper"
                    placeholder="Ottawa fast-tracks critical-minerals permits…"
                  />
                </label>
                <label className="block">
                  <span className="bb-overline text-bb-slate">Excerpt</span>
                  <textarea
                    value={form.excerpt}
                    onChange={setField("excerpt")}
                    data-testid="draft-excerpt"
                    rows={2}
                    className="mt-1 w-full border border-border px-3 py-2 text-sm text-bb-slate focus:outline-none focus:border-bb-copper resize-y"
                    placeholder="Short summary shown on the news list."
                  />
                </label>
                <label className="block">
                  <span className="bb-overline text-bb-slate">Body</span>
                  <textarea
                    value={form.content}
                    onChange={setField("content")}
                    data-testid="draft-content"
                    rows={14}
                    className="mt-1 w-full border border-border px-3 py-3 text-sm leading-relaxed text-bb-navy focus:outline-none focus:border-bb-copper font-plex resize-y"
                    placeholder="Write your article. Blank lines separate paragraphs."
                  />
                </label>
              </div>

              {contentPreviewParagraphs.length > 0 && (
                <div className="bg-white border border-border p-4">
                  <div className="bb-overline text-bb-copper">Preview</div>
                  <h1 className="mt-2 font-archivo font-bold text-2xl text-bb-navy leading-tight">
                    {form.title || "Untitled"}
                  </h1>
                  {form.excerpt && (
                    <p className="mt-2 text-bb-slate italic">{form.excerpt}</p>
                  )}
                  <div className="mt-3 space-y-3">
                    {contentPreviewParagraphs.slice(0, 6).map((p, i) => (
                      <p key={i} className="text-sm text-bb-navy leading-relaxed">{p}</p>
                    ))}
                    {contentPreviewParagraphs.length > 6 && (
                      <p className="text-xs font-mono text-bb-slate">… {contentPreviewParagraphs.length - 6} more paragraph(s)</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <aside className="lg:col-span-1 space-y-4">
              <div className="bg-white border border-border p-4 space-y-3">
                <div className="bb-overline text-bb-copper">Metadata</div>
                <label className="block">
                  <span className="bb-overline text-bb-slate">Category</span>
                  <select
                    value={form.category}
                    onChange={setField("category")}
                    data-testid="draft-category"
                    className="mt-1 w-full border border-border px-3 py-2 text-sm bg-white focus:outline-none focus:border-bb-copper"
                  >
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="bb-overline text-bb-slate">Slug (auto if blank)</span>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={setField("slug")}
                    data-testid="draft-slug"
                    className="mt-1 w-full border border-border px-3 py-2 text-xs font-mono focus:outline-none focus:border-bb-copper"
                    placeholder="ottawa-fast-tracks-permits"
                  />
                </label>
                <label className="block">
                  <span className="bb-overline text-bb-slate">Author</span>
                  <input
                    type="text"
                    value={form.author}
                    onChange={setField("author")}
                    data-testid="draft-author"
                    className="mt-1 w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-bb-copper"
                  />
                </label>
                <label className="block">
                  <span className="bb-overline text-bb-slate">Featured image URL</span>
                  <input
                    type="url"
                    value={form.featuredImage}
                    onChange={setField("featuredImage")}
                    data-testid="draft-featured-image"
                    className="mt-1 w-full border border-border px-3 py-2 text-xs font-mono focus:outline-none focus:border-bb-copper"
                    placeholder="https://images.unsplash.com/…"
                  />
                </label>
              </div>

              <div className="bg-white border border-border p-4 space-y-3">
                <div className="bb-overline text-bb-copper">Cross-links</div>
                <label className="block">
                  <span className="bb-overline text-bb-slate">Region</span>
                  <select
                    value={form.region}
                    onChange={setField("region")}
                    data-testid="draft-region"
                    className="mt-1 w-full border border-border px-3 py-2 text-sm bg-white focus:outline-none focus:border-bb-copper"
                  >
                    <option value="">— none —</option>
                    {regions.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="bb-overline text-bb-slate">Commodity</span>
                  <select
                    value={form.commodity}
                    onChange={setField("commodity")}
                    data-testid="draft-commodity"
                    className="mt-1 w-full border border-border px-3 py-2 text-sm bg-white focus:outline-none focus:border-bb-copper"
                  >
                    <option value="">— none —</option>
                    {commodities.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="bb-overline text-bb-slate">Company</span>
                  <select
                    value={form.company}
                    onChange={setField("company")}
                    data-testid="draft-company"
                    className="mt-1 w-full border border-border px-3 py-2 text-sm bg-white focus:outline-none focus:border-bb-copper"
                  >
                    <option value="">— none —</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="bb-overline text-bb-slate">Project</span>
                  <select
                    value={form.project}
                    onChange={setField("project")}
                    data-testid="draft-project"
                    className="mt-1 w-full border border-border px-3 py-2 text-sm bg-white focus:outline-none focus:border-bb-copper"
                  >
                    <option value="">— none —</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="bg-white border border-border p-4 space-y-2">
                <div className="bb-overline text-bb-copper">Status</div>
                <div className="text-sm text-bb-slate">
                  {current?.status === "published" ? (
                    <>
                      <span className="bb-tag-positive text-[10px] font-mono px-2 py-0.5 mr-2">PUBLISHED</span>
                      <Link
                        to={`/news/${current.slug}`}
                        target="_blank"
                        className="inline-flex items-center gap-1 text-[11px] font-mono text-bb-navy bb-link"
                        data-testid="draft-view-live"
                      >
                        <ExternalLink size={11} /> View live
                      </Link>
                    </>
                  ) : (
                    <span className="bb-tag-neutral text-[10px] font-mono px-2 py-0.5">DRAFT</span>
                  )}
                </div>
                <div className="flex flex-col gap-2 pt-2">
                  <button
                    onClick={() => save({ publishAfter: false })}
                    disabled={!canSave || saving || publishing}
                    data-testid="draft-save"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-border text-bb-navy text-sm font-archivo uppercase tracking-widest disabled:opacity-50 min-h-[44px]"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save draft
                  </button>
                  <button
                    onClick={() => save({ publishAfter: true })}
                    disabled={!canSave || saving || publishing}
                    data-testid="draft-save-publish"
                    className="bb-btn-primary inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm disabled:opacity-50 min-h-[44px]"
                  >
                    {publishing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Save & publish
                  </button>
                </div>
              </div>
            </aside>
          </>
        )}
      </div>
    </div>
  );
}
